import React, { useState } from 'react';
import { ChatbotMessage, isLandscapeCard } from '../../models/chatbot/types';
import ReferenceCard from './ReferenceCard';
import ChatbotMarkdown from './ChatbotMarkdown';
import { Button, Icon } from '@socialchorus/shared-ui-components';
import { useTranslation } from 'react-i18next';

const MAX_VISIBLE_REFERENCES = 3;

interface MessageBubbleProps {
  message: ChatbotMessage;
  isStreaming?: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isStreaming = false,
}) => {
  const { t } = useTranslation();
  const isUser = message.role === 'user';
  const displayContent = message.content || '';
  const [isReferencesExpanded, setIsReferencesExpanded] = useState(false);

  const hasReferences = message.references && message.references.length > 0;
  const shouldCollapseReferences =
    message.references && message.references.length > MAX_VISIBLE_REFERENCES;
  const displayedReferences =
    shouldCollapseReferences && !isReferencesExpanded
      ? message.references?.slice(0, MAX_VISIBLE_REFERENCES) || []
      : message.references || [];
  const hasLandscapeCards = displayedReferences.some(
    (ref) => ref.card && isLandscapeCard(ref.card)
  );

  const formatTime = (date: string) => {
    const dateObj = new Date(date);
    return dateObj.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getISODateTime = (date: string) => {
    return new Date(date).toISOString();
  };

  return (
    <div
      className={`chatbot__message ${
        isUser ? 'chatbot__message--user' : 'chatbot__message--assistant'
      } ${isStreaming ? 'chatbot__message--streaming' : ''}`}
      data-testid={
        isUser
          ? 'playwright-chatbot-user-message'
          : 'playwright-chatbot-assistant-message'
      }
    >
      <div
        className={`chatbot__message-bubble ${
          isUser
            ? 'chatbot__message-bubble--user'
            : 'chatbot__message-bubble--assistant'
        }`}
      >
        <div className="chatbot__message-content">
          {isUser ? (
            <div className="chatbot__message-text">{displayContent}</div>
          ) : (
            <ChatbotMarkdown content={displayContent} />
          )}
          {hasReferences && (
            <nav aria-label={t('chatbot.references.title')}>
              <div
                className={`chatbot__references ${
                  shouldCollapseReferences && !isReferencesExpanded
                    ? 'chatbot__references--collapsed'
                    : ''
                } ${
                  hasLandscapeCards ? 'chatbot__references--has-landscape' : ''
                }`}
              >
                {displayedReferences.map((reference, index) => (
                  <ReferenceCard
                    key={`${reference.referenceType}-${reference.referenceId}-${index}`}
                    referenceType={reference.referenceType}
                    referenceId={reference.referenceId}
                    card={reference.card}
                  />
                ))}
              </div>
              {shouldCollapseReferences && (
                <div className="chatbot__references-toggle">
                  <Button
                    variant="text"
                    size="compact"
                    onClick={() =>
                      setIsReferencesExpanded(!isReferencesExpanded)
                    }
                    rightIcon={<Icon>expand_more</Icon>}
                    fullWidth={true}
                    label={
                      isReferencesExpanded
                        ? t('chatbot.references.see_less')
                        : t('chatbot.references.see_more', {
                            count:
                              (message.references?.length || 0) -
                              MAX_VISIBLE_REFERENCES,
                          })
                    }
                    className="chatbot__references-toggle-button"
                  />
                </div>
              )}
            </nav>
          )}
        </div>
        {!isStreaming && (
          <time
            className={`chatbot__message-timestamp ${
              isUser
                ? 'chatbot__message-timestamp--user'
                : 'chatbot__message-timestamp--assistant'
            }`}
            dateTime={getISODateTime(message.createdAt)}
          >
            {formatTime(message.createdAt)}
          </time>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
